import React, { useState, useCallback, useEffect } from "react";
import FormInput from "../../components/CustomComponents/Input/formInput.jsx";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import CustomModal from "../../components/CustomComponents/modal/customModal";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery.jsx";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from '../../hooks/useThemeColor';
import swal from "sweetalert";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles.js";
import CountryCitySelector from "../../components/CustomComponents/DropdownSelections/CountryCitySelector.jsx";
import { extractStaffFormErrors } from "../../utils/staffApiErrors.js";
import "./UpdateCompany.css";

const OPEN_COMPANY_STAFF_ADD_MODAL = "openCompanyStaffAddModal";

const CompanyStaff = ({
  CompID,
  companyCountry = "",
  companyCity = "",
  showAddForm: externalShowAddForm,
  setShowAddForm: externalSetShowAddForm,
}) => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [internalShowAddForm, setInternalShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  
  // Use external control if provided, otherwise use internal state
  const showAddForm = externalShowAddForm !== undefined ? externalShowAddForm : internalShowAddForm;
  const setShowAddForm = externalSetShowAddForm || setInternalShowAddForm;
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState(companyCountry || "");
  const [selectedCity, setSelectedCity] = useState(companyCity || "");
  const [staffData, setStaffData] = useState({
    UserName: "",
    FirstName: "",
    LastName: "",
    Email: "",
    PhoneNo: "",
    PassCode: "",
    UserRoles: null,
    Active: true,
    CompID: parseInt(CompID),
    Password: "",
    ConfirmPassword: "",
    Country: companyCountry || "",
    Address: "",
    City: companyCity || "",
    PostalCode: "",
    Gender: "",
    DateOfBirth: null,
    RatePerHour: 0,
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordModalUserName, setPasswordModalUserName] = useState("");
  const [newLoginPassword, setNewLoginPassword] = useState("");
  const [confirmLoginPassword, setConfirmLoginPassword] = useState("");
  const [passwordFieldErrors, setPasswordFieldErrors] = useState({});
  const [revealNewPassword, setRevealNewPassword] = useState(false);
  const [revealConfirmPassword, setRevealConfirmPassword] = useState(false);

  // Fetch roles
  const fetchRoles = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ROLES, {
        params: {
          CompID: CompID,
        },
      });
      const roleData = response.data.data || response.data;

      const rolesOptions = roleData.map((role) => ({
        value: role.comRolePermission_ID,
        label: role.r_Title,
      }));
      setRoles(rolesOptions);
    } catch (err) {
      console.error("Error fetching roles:", err);
      showErrorToast("Failed to fetch roles");
    }
  }, [CompID]);

  // Handle country/city changes — keep country locked to company information
  const handleCountryCityChange = useCallback((data) => {
    setStaffData((prev) => ({
      ...prev,
      ...data,
      Country: companyCountry || data.Country || prev.Country || "",
    }));
    if (data.Country !== undefined) {
      setSelectedCountry(data.Country);
    } else if (companyCountry) {
      setSelectedCountry(companyCountry);
    }
    if (data.City !== undefined) {
      setSelectedCity(data.City);
    }
    setFieldErrors((prev) => {
      const next = { ...prev };
      if (data.Country !== undefined || companyCountry) delete next.Country;
      if (data.City !== undefined) delete next.City;
      return next;
    });
  }, [companyCountry]);

  // Fetch company staff
  const fetchCompanyStaff = useCallback(async () => {
    try {
      setLoading(true);
      console.log("Fetching staff for CompID:", CompID);
      const response = await axiosInstance.get(APIs.GET_COMPANY_STAFF, {
        params: {
          CompID: CompID,
          SortBy: "UserName",
          SortOrder: "ASC",
          Search: "",
          PageSize: "100",
          PageNumber: 1,
        },
      });
 
      const staffData = response.data.data || response.data;

      if (Array.isArray(staffData)) {
        setStaffList(staffData);
        console.log("Staff list set:", staffData);
      } else {
        console.warn("No staff data found or unexpected format:", staffData);
        setStaffList([]);
      }
    } catch (error) {
      console.error("Error fetching staff:", error);
      showErrorToast("Failed to fetch staff members");
      setStaffList([]);
    } finally {
      setLoading(false);
    }
  }, [CompID]);

  const handleInputChange = useCallback((e, field) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setStaffData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const prepareAddStaffForm = useCallback(() => {
    const defaultCountry = companyCountry || "";
    const defaultCity = companyCity || "";
    setEditingStaff(null);
    setStaffData({
      UserName: "",
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNo: "",
      PassCode: "",
      UserRoles: null,
      Active: true,
      CompID: parseInt(CompID, 10),
      Password: "",
      ConfirmPassword: "",
      Country: defaultCountry,
      Address: "",
      City: defaultCity,
      PostalCode: "",
      Gender: "",
      DateOfBirth: null,
      RatePerHour: 0,
    });
    setSelectedRole(null);
    setSelectedCountry(defaultCountry);
    setSelectedCity(defaultCity);
    setFieldErrors({});
  }, [CompID, companyCountry, companyCity]);

  useEffect(() => {
    const onParentOpenAdd = () => {
      prepareAddStaffForm();
      setShowAddForm(true);
    };
    window.addEventListener(OPEN_COMPANY_STAFF_ADD_MODAL, onParentOpenAdd);
    return () => window.removeEventListener(OPEN_COMPANY_STAFF_ADD_MODAL, onParentOpenAdd);
  }, [prepareAddStaffForm, setShowAddForm]);

  // If the modal opens before the company details finish loading (common after newly creating a company),
  // re-sync staff defaults once companyCountry/companyCity props become available.
  useEffect(() => {
    if (!showAddForm) return; // only when creating/edit modal is open
    if (editingStaff) return; // don't override edit values

    if (companyCountry) {
      setSelectedCountry(companyCountry);
      setStaffData((prev) => ({ ...prev, Country: companyCountry }));
    }

    if (companyCity) {
      setSelectedCity(companyCity);
      setStaffData((prev) => ({ ...prev, City: companyCity }));
    }

    // Clear any "missing city" validation that may have appeared before props loaded.
    setFieldErrors((prev) => {
      if (!prev?.City && !prev?.Country) return prev;
      const next = { ...prev };
      delete next.City;
      delete next.Country;
      return next;
    });
  }, [showAddForm, editingStaff, companyCountry, companyCity]);

  const handleAddStaff = async () => {
    const { UserName, FirstName, Email, PassCode, Password, ConfirmPassword } = staffData;
    setFieldErrors({});

    const clientErrors = {};
    if (!UserName.trim()) clientErrors.UserName = "Username is required.";
    if (!FirstName.trim()) clientErrors.FirstName = "First name is required.";
    if (!Email || !validateEmail(Email)) clientErrors.Email = "A valid email is required.";
    if (!PassCode.trim()) clientErrors.PassCode = "Pass code is required.";
    if (!Password) clientErrors.Password = "Password is required.";
    if (Password && Password !== ConfirmPassword) clientErrors.ConfirmPassword = "Passwords do not match.";
    const roleId =
      staffData.UserRoles != null && staffData.UserRoles !== "" ? Number(staffData.UserRoles) : 0;
    if (!roleId) clientErrors.UserRoles = "Please select a role.";
    if (!(staffData.Country ?? "").trim() && !companyCountry) clientErrors.Country = "Country is required.";
    if (!(staffData.City ?? "").trim()) clientErrors.City = "City is required.";

    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      showErrorToast("Please fix the highlighted fields.");
      return;
    }

    const addPayload = {
      UserName: staffData.UserName.trim(),
      FirstName: staffData.FirstName.trim(),
      LastName: staffData.LastName.trim(),
      Email: staffData.Email.trim(),
      PhoneNo: staffData.PhoneNo ?? "",
      PassCode: staffData.PassCode.trim(),
      Country: companyCountry || staffData.Country || "",
      City: staffData.City ?? "",
      Active: staffData.Active,
      CompID: parseInt(CompID, 10),
      ComRolePermission_ID: roleId,
      RatePerHour: staffData.RatePerHour ?? 0,
      Password: staffData.Password,
    };

    try {
      setLoading(true);
      const response = await axiosInstance.post(APIs.ADD_STAFF, addPayload);

      if (response.status >= 400) {
        const { fieldErrors: fe, summary } = extractStaffFormErrors(response.data);
        setFieldErrors(fe);
        showErrorToast(summary);
        return;
      }

      if (response.data.status) {
        showSuccessToast("Staff member added successfully");
        setShowAddForm(false);
        resetForm();
        fetchCompanyStaff();
      } else {
        const { fieldErrors: fe, summary } = extractStaffFormErrors(response.data);
        setFieldErrors(fe);
        showErrorToast(summary || response.data.message || "Error adding staff member");
      }
    } catch (err) {
      const data = err.response?.data;
      const { fieldErrors: fe, summary } = extractStaffFormErrors(data || {});
      setFieldErrors(fe);
      showErrorToast(summary || err.message || "Error adding staff member");
      console.error("Error adding staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const openChangePasswordModal = (staff) => {
    setPasswordModalUserName(staff.userName || "");
    setNewLoginPassword("");
    setConfirmLoginPassword("");
    setPasswordFieldErrors({});
    setRevealNewPassword(false);
    setRevealConfirmPassword(false);
    setShowPasswordModal(true);
  };

  const closeChangePasswordModal = () => {
    setShowPasswordModal(false);
    setPasswordModalUserName("");
    setNewLoginPassword("");
    setConfirmLoginPassword("");
    setPasswordFieldErrors({});
    setRevealNewPassword(false);
    setRevealConfirmPassword(false);
  };

  const handleChangePasswordSubmit = async () => {
    setPasswordFieldErrors({});
    const err = {};
    if (!newLoginPassword?.trim()) err.Password = "New password is required.";
    if (newLoginPassword !== confirmLoginPassword) {
      err.ConfirmPassword = "Passwords do not match.";
    }
    if (Object.keys(err).length > 0) {
      setPasswordFieldErrors(err);
      showErrorToast("Please fix the password fields.");
      return;
    }

    try {
      setLoading(true);
      const pwdRes = await axiosInstance.post(
        `${APIs.UPDATE_PASSWORD}?CompID=${parseInt(CompID, 10)}`,
        {
          userName: passwordModalUserName.trim(),
          password: newLoginPassword,
          confirmPassword: confirmLoginPassword,
        }
      );

      if (pwdRes.status >= 400 || pwdRes.data?.status === false) {
        const { fieldErrors: fe, summary } = extractStaffFormErrors(pwdRes.data || {});
        setPasswordFieldErrors(fe);
        showErrorToast(summary || pwdRes.data?.message || "Could not update password.");
        return;
      }

      showSuccessToast(pwdRes.data?.message || "Password updated successfully.");
      closeChangePasswordModal();
    } catch (e) {
      const data = e.response?.data;
      const { fieldErrors: fe, summary } = extractStaffFormErrors(data || {});
      setPasswordFieldErrors(fe);
      showErrorToast(summary || e.message || "Could not update password.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStaff = async () => {
    const { UserName, FirstName, Email, PassCode } = staffData;
    setFieldErrors({});

    const clientErrors = {};
    if (!UserName.trim()) clientErrors.UserName = "Username is required.";
    if (!FirstName.trim()) clientErrors.FirstName = "First name is required.";
    if (!Email || !validateEmail(Email)) clientErrors.Email = "A valid email is required.";
    if (!PassCode.trim()) clientErrors.PassCode = "Pass code is required.";
    const roleId =
      staffData.UserRoles != null && staffData.UserRoles !== ""
        ? Number(staffData.UserRoles)
        : editingStaff?.comRolePermission_ID != null
          ? Number(editingStaff.comRolePermission_ID)
          : 0;
    if (!roleId) clientErrors.UserRoles = "Please select a role.";
    if (!(staffData.Country ?? "").trim() && !companyCountry) clientErrors.Country = "Country is required.";
    if (!(staffData.City ?? "").trim()) clientErrors.City = "City is required.";

    if (Object.keys(clientErrors).length > 0) {
      setFieldErrors(clientErrors);
      showErrorToast("Please fix the highlighted fields.");
      return;
    }

    const updatePayload = {
      UserName: staffData.UserName.trim(),
      FirstName: staffData.FirstName.trim(),
      LastName: staffData.LastName.trim(),
      Email: staffData.Email.trim(),
      PhoneNo: staffData.PhoneNo ?? "",
      PassCode: staffData.PassCode.trim(),
      Country: companyCountry || staffData.Country || "",
      City: staffData.City ?? "",
      Active: staffData.Active,
      CompID: parseInt(CompID, 10),
      ComRolePermission_ID: roleId,
      RatePerHour: staffData.RatePerHour ?? 0,
      Password: "",
    };

    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.UPDATE_STAFF, updatePayload);

      if (response.status >= 400) {
        const { fieldErrors: fe, summary } = extractStaffFormErrors(response.data);
        setFieldErrors(fe);
        showErrorToast(summary);
        return;
      }

      if (!response.data.status) {
        const { fieldErrors: fe, summary } = extractStaffFormErrors(response.data);
        setFieldErrors(fe);
        showErrorToast(summary || response.data.message || "Error updating staff member");
        return;
      }

      showSuccessToast("Staff member updated successfully");
      setEditingStaff(null);
      resetForm();
      fetchCompanyStaff();
    } catch (err) {
      const data = err.response?.data;
      const { fieldErrors: fe, summary } = extractStaffFormErrors(data || {});
      setFieldErrors(fe);
      showErrorToast(summary || err.message || "Error updating staff member");
      console.error("Error updating staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStaff = async (staff) => {
    const confirmDelete = await swal({
      title: "Are you sure?",
      text: `Are you sure you want to delete ${staff.firstName} ${staff.lastName}?`,
      icon: "warning",
      buttons: true,
      dangerMode: true,
    });

    if (!confirmDelete) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_COMPANY_STAFF, {
        params: { 
          CompID: CompID,
          UserName: staff.userName 
        },
      });

      if (response.data.status) {
        showSuccessToast("Staff member deleted successfully");
        fetchCompanyStaff();
      } else {
        showErrorToast(response.data.message || "Error deleting staff member");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Error deleting staff member");
      console.error("Error deleting staff:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditStaff = (staff) => {
    setFieldErrors({});
    setEditingStaff(staff);
    
    // Find and set the selected role
    const staffRole = roles.find((r) => r.value === staff.comRolePermission_ID);
    setSelectedRole(staffRole || null);

    // Use company country/city (same as company information); keep staff city if already set
    const country = companyCountry || staff.country || "";
    const city = staff.city || companyCity || "";
    
    setSelectedCountry(country);
    setSelectedCity(city);
    
    setStaffData({
      UserName: staff.userName || "",
      FirstName: staff.firstName || "",
      LastName: staff.lastName || "",
      Email: staff.email || "",
      PhoneNo: staff.phoneNo || "",
      PassCode: staff.passCode || "",
      UserRoles: staff.comRolePermission_ID || null,
      Active: staff.active !== false,
      CompID: parseInt(CompID),
      Password: "",
      ConfirmPassword: "",
      Country: country,
      Address: staff.address || "",
      City: city,
      PostalCode: staff.postalCode || "",
      Gender: staff.gender || "",
      DateOfBirth: staff.dateOfBirth || null,
      RatePerHour: staff.ratePerHour || 0,
    });
    setShowAddForm(true);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to get role name by ID
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.value === roleId);
    return role ? role.label : 'N/A';
  };

  // Handle status toggle
  const handleStatusToggle = async (staff) => {
    const newStatus = !staff.active;
    
    try {
      setLoading(true);
      const response = await axiosInstance.put(APIs.UPDATE_STAFF, {
        UserName: staff.userName,
        FirstName: staff.firstName,
        LastName: staff.lastName,
        Email: staff.email,
        PhoneNo: staff.phoneNo ?? "",
        PassCode: staff.passCode || "",
        ComRolePermission_ID: staff.comRolePermission_ID ?? 0,
        Active: newStatus,
        CompID: parseInt(CompID, 10),
        Country: staff.country || "",
        City: staff.city || "",
        RatePerHour: staff.ratePerHour ?? 0,
        Password: "",
      });

      if (response.status >= 400) {
        const { summary } = extractStaffFormErrors(response.data || {});
        showErrorToast(summary);
        return;
      }

      if (response.data.status) {
        showSuccessToast(`Staff ${newStatus ? 'activated' : 'deactivated'} successfully`);
        fetchCompanyStaff();
      } else {
        const { summary } = extractStaffFormErrors(response.data || {});
        showErrorToast(summary || response.data.message || "Failed to update staff status");
      }
    } catch (err) {
      const { summary } = extractStaffFormErrors(err.response?.data || {});
      showErrorToast(summary || err.response?.data?.message || "Error updating staff status");
      console.error("Error updating staff status:", err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    const defaultCountry = companyCountry || "";
    const defaultCity = companyCity || "";
    setFieldErrors({});
    setStaffData({
      UserName: "",
      FirstName: "",
      LastName: "",
      Email: "",
      PhoneNo: "",
      PassCode: "",
      UserRoles: null,
      Active: true,
      CompID: parseInt(CompID),
      Password: "",
      ConfirmPassword: "",
      Country: defaultCountry,
      Address: "",
      City: defaultCity,
      PostalCode: "",
      Gender: "",
      DateOfBirth: null,
      RatePerHour: 0,
    });
    setSelectedRole(null);
    setSelectedCountry(defaultCountry);
    setSelectedCity(defaultCity);
    setEditingStaff(null);
    setShowAddForm(false);
  };

  useEffect(() => {
    fetchRoles();
    fetchCompanyStaff();
  }, [fetchRoles, fetchCompanyStaff]);

  return (
    <div className="uc-page">
      {loading && <Preloader />}

      {/* Staff List */}
      <section className="uc-section uc-section--nested">
        <div className="uc-section__header">
          <h5>Staff Members</h5>
          <p>Manage users for this company</p>
        </div>
        <div className="uc-section__body">
          {staffList.length === 0 ? (
            <div className="uc-empty">
              <i className="fa fa-users" aria-hidden />
              <div>No staff members found for this company.</div>
            </div>
          ) : (
            <div className="uc-table-wrap">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Pass Code</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staffList.map((staff, index) => (
                    <tr key={index}>
                      <td>{staff.userName}</td>
                      <td>{staff.firstName} {staff.lastName}</td>
                      <td>{staff.email}</td>
                      <td>{staff.phoneNo || staff.phone}</td>
                      <td>{staff.passCode || 'N/A'}</td>
                      <td>{getRoleName(staff.comRolePermission_ID)}</td>
                      <td>
                        <ToggleSwitch
                          isChecked={staff.active !== false}
                          onToggle={() => handleStatusToggle(staff)}
                          label=""
                          name={`staff-status-${staff.userName}`}
                        />
                      </td>
                      <td>
                        <button
                          type="button"
                          title="Edit staff"
                          className="uc-icon-btn me-2"
                          style={{
                            border: `1px solid ${themeColor}`,
                            color: themeColor,
                          }}
                          onClick={() => handleEditStaff(staff)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = themeColor;
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = themeColor;
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                        </button>
                        <button
                          type="button"
                          title="Change login password"
                          className="uc-icon-btn me-2"
                          style={{
                            border: '1px solid #6c757d',
                            color: '#6c757d',
                          }}
                          onClick={() => openChangePasswordModal(staff)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#6c757d';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#6c757d';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa fa-key" style={{ fontSize: '12px' }}></i>
                        </button>
                        <button
                          type="button"
                          title="Delete staff"
                          className="uc-icon-btn"
                          style={{
                            border: '1px solid #dc3545',
                            color: '#dc3545',
                          }}
                          onClick={() => handleDeleteStaff(staff)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#dc3545';
                            e.currentTarget.style.color = 'white';
                            e.currentTarget.style.transform = 'scale(1.05)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#dc3545';
                            e.currentTarget.style.transform = 'scale(1)';
                          }}
                        >
                          <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Add/Edit Staff Form Modal */}
      <CustomModal
        isOpen={showAddForm}
        onClose={resetForm}
        isCentered
        modalSize="modal-lg"
        maxHeight="85vh"
        title={editingStaff ? "Edit Staff Member" : "Add New Staff Member"}
        subtitle={
          editingStaff
            ? "Update the details for this staff member"
            : "Create a new user account for this company"
        }
        showButton
        submitButtonText={editingStaff ? "Update Staff" : "Add Staff"}
        submitButtonStyle={{ width: "auto", minWidth: "140px", whiteSpace: "nowrap" }}
        submitButtonDisabled={loading}
        onSubmit={editingStaff ? handleUpdateStaff : handleAddStaff}
      >
        <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Username *"
                      placeHolder="Enter Username"
                      type="text"
                      value={staffData.UserName}
                      onchange={(e) => handleInputChange(e, "UserName")}
                      disabled={editingStaff !== null}
                      error={fieldErrors.UserName}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Email *"
                      placeHolder="Enter Email Address"
                      type="email"
                      value={staffData.Email}
                      onchange={(e) => handleInputChange(e, "Email")}
                      error={fieldErrors.Email}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="First Name *"
                      placeHolder="Enter First Name"
                      type="text"
                      value={staffData.FirstName}
                      onchange={(e) => handleInputChange(e, "FirstName")}
                      error={fieldErrors.FirstName}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Last Name"
                      placeHolder="Enter Last Name"
                      type="text"
                      value={staffData.LastName}
                      onchange={(e) => handleInputChange(e, "LastName")}
                      error={fieldErrors.LastName}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Phone Number"
                      placeHolder="Enter Phone Number"
                      type="tel"
                      value={staffData.PhoneNo}
                      onchange={(e) => handleInputChange(e, "PhoneNo")}
                      error={fieldErrors.PhoneNo}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Pass Code *"
                      placeHolder="Enter Pass Code"
                      type="text"
                      value={staffData.PassCode}
                      onchange={(e) => handleInputChange(e, "PassCode")}
                      error={fieldErrors.PassCode}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <label className="uc-label">Role *</label>
                    <Select
                      value={selectedRole}
                      onChange={(option) => {
                        setSelectedRole(option);
                        setStaffData({
                          ...staffData,
                          UserRoles: option ? option.value : null,
                        });
                        setFieldErrors((prev) => {
                          if (!prev.UserRoles) return prev;
                          const next = { ...prev };
                          delete next.UserRoles;
                          return next;
                        });
                      }}
                      options={roles}
                      styles={dropdownStyles}
                      placeholder="Select Role"
                      isClearable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldBlockScroll={false}
                      menuShouldScrollIntoView={false}
                      closeMenuOnScroll={false}
                    />
                    {fieldErrors.UserRoles ? (
                      <div className="small mt-1" style={{ color: "#e74c3c" }}>
                        {fieldErrors.UserRoles}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="row mb-3">
                  <CountryCitySelector
                    selectedCountry={selectedCountry || companyCountry}
                    selectedCity={selectedCity}
                    setSelectedCity={setSelectedCity}
                    onChange={handleCountryCityChange}
                    setLoading={null}
                    selectedValueIsLabel={true}
                    countryFieldName="Country"
                    cityFieldName="City"
                    cityLabel="City *"
                    cityPlaceholder="Select City"
                    showCountryField={false}
                    showCityField={true}
                  />
                  {(fieldErrors.Country || fieldErrors.City) && (
                    <div className="col-12 small mt-1" style={{ color: "#e74c3c" }}>
                      {[fieldErrors.Country, fieldErrors.City].filter(Boolean).join(" ")}
                    </div>
                  )}
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <FormInput
                      label="Address"
                      placeHolder="Enter Address"
                      type="text"
                      value={staffData.Address}
                      onchange={(e) => handleInputChange(e, "Address")}
                      error={fieldErrors.Address}
                    />
                  </div>
                  <div className="col-6">
                    <FormInput
                      label="Rate Per Hour"
                      placeHolder="Enter Rate Per Hour"
                      type="number"
                      value={staffData.RatePerHour}
                      onchange={(e) => handleInputChange(e, "RatePerHour")}
                      step="0.01"
                      error={fieldErrors.RatePerHour}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-6">
                    <ToggleSwitch
                      isChecked={staffData.Active}
                      onToggle={(e) => handleInputChange(e, "Active")}
                      label="Active Status"
                      name="Active"
                    />
                  </div>
                </div>

                {!editingStaff && (
                  <div className="row mb-3">
                    <div className="col-6">
                      <FormInput
                        label="Password"
                        placeHolder="Enter Password"
                        type="password"
                        value={staffData.Password}
                        onchange={(e) => handleInputChange(e, "Password")}
                        error={fieldErrors.Password}
                      />
                    </div>
                    <div className="col-6">
                      <FormInput
                        label="Confirm Password"
                        placeHolder="Confirm Password"
                        type="password"
                        value={staffData.ConfirmPassword}
                        onchange={(e) => handleInputChange(e, "ConfirmPassword")}
                        error={fieldErrors.ConfirmPassword}
                      />
                    </div>
                  </div>
                )}
      </CustomModal>

      {/* Change login password — separate from edit profile */}
      <CustomModal
        isOpen={showPasswordModal}
        onClose={closeChangePasswordModal}
        isCentered
        dialogMaxWidth="440px"
        maxHeight="85vh"
        title="Change login password"
        subtitle={`User: ${passwordModalUserName}`}
        showButton
        submitButtonText="Update password"
        submitButtonStyle={{ width: "auto", minWidth: "140px", whiteSpace: "nowrap" }}
        submitButtonDisabled={loading}
        onSubmit={handleChangePasswordSubmit}
      >
        <div className="row mb-3">
          <div className="col-12 mb-2">
            <FormInput
              label="New password"
              placeHolder="New password"
              type={revealNewPassword ? "text" : "password"}
              value={newLoginPassword}
              onchange={(e) => {
                setNewLoginPassword(e.target.value);
                setPasswordFieldErrors((prev) => {
                  const n = { ...prev };
                  delete n.Password;
                  return n;
                });
              }}
              error={passwordFieldErrors.Password}
              icon={revealNewPassword ? "fa-eye-slash" : "fa-eye"}
              iconOnClick={() => setRevealNewPassword((v) => !v)}
              iconTitle={revealNewPassword ? "Hide password" : "Show password"}
            />
          </div>
          <div className="col-12">
            <FormInput
              label="Confirm new password"
              placeHolder="Confirm new password"
              type={revealConfirmPassword ? "text" : "password"}
              value={confirmLoginPassword}
              onchange={(e) => {
                setConfirmLoginPassword(e.target.value);
                setPasswordFieldErrors((prev) => {
                  const n = { ...prev };
                  delete n.ConfirmPassword;
                  return n;
                });
              }}
              error={passwordFieldErrors.ConfirmPassword}
              icon={revealConfirmPassword ? "fa-eye-slash" : "fa-eye"}
              iconOnClick={() => setRevealConfirmPassword((v) => !v)}
              iconTitle={revealConfirmPassword ? "Hide password" : "Show password"}
            />
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default CompanyStaff;

